import api from './api';

const API_BASE = '/sales-radar';

/**
 * Sales Radar Service
 * Handles all Sales Radar related API calls
 */
export const salesRadarService = {
  // ==================== Leads ====================
  
  /**
   * Get paginated list of leads
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (1-indexed)
   * @param {number} params.per_page - Items per page
   * @param {string} params.sort_by - Sort field
   * @param {string} params.sort_order - Sort order (asc/desc)
   * @param {string} params.search - Search query
   * @param {string} params.stage - Filter by stage
   * @param {string} params.score_range - Filter by score range (hot/warm/cool/cold)
   * @returns {Promise<Object>} - { leads, total, page, per_page, summary }
   */
  async getLeads(params = {}) {
    const response = await api.get(`${API_BASE}/leads`, { params });
    return response.data;
  },

  /**
   * Get a single lead by ID
   * @param {number} leadId - Lead ID
   * @returns {Promise<Object>} - Lead details
   */
  async getLead(leadId) {
    const response = await api.get(`${API_BASE}/leads/${leadId}`);
    return response.data;
  },

  /**
   * Update a lead
   * @param {number} leadId - Lead ID
   * @param {Object} data - Update data
   * @returns {Promise<Object>} - Updated lead
   */
  async updateLead(leadId, data) {
    const response = await api.patch(`${API_BASE}/leads/${leadId}`, data);
    return response.data;
  },

  /**
   * Delete a lead
   * @param {number} leadId - Lead ID
   * @returns {Promise<void>}
   */
  async deleteLead(leadId) {
    await api.delete(`${API_BASE}/leads/${leadId}`);
  },

  /**
   * Export leads to CSV
   * @param {Object} params - Export parameters
   * @returns {Promise<Blob>} - CSV file
   */
  async exportLeads(params = {}) {
    const response = await api.post(`${API_BASE}/leads/export`, params, {
      responseType: 'blob',
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `leads-export-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return response.data;
  },

  // ==================== Lead Stages ====================

  /**
   * Update lead stage
   * @param {number} leadId - Lead ID
   * @param {string} stage - New stage
   * @param {string} reason - Reason for change (optional)
   * @returns {Promise<Object>} - Updated lead
   */
  async updateLeadStageForLead(leadId, stage, reason = null) {
    const response = await api.post(`${API_BASE}/leads/${leadId}/stage`, {
      stage,
      reason,
    });
    return response.data;
  },

  /**
   * Get stage history for a lead
   * @param {number} leadId - Lead ID
   * @returns {Promise<Array>} - Stage history
   */
  async getStageHistory(leadId) {
    const response = await api.get(`${API_BASE}/leads/${leadId}/stage-history`);
    return response.data;
  },

  /**
   * Get available stages
   * @returns {Promise<Array>} - List of stages
   */
  async getStages() {
    const response = await api.get(`${API_BASE}/stages`);
    return response.data;
  },

  /**
   * Get all lead stages configuration
   * @returns {Promise<Object>} - Stages data
   */
  async getLeadStages() {
    const response = await api.get(`${API_BASE}/lead-stages`);
    return response.data;
  },

  /**
   * Create a lead stage
   * @param {Object} stageData - Stage data
   * @returns {Promise<Object>} - Created stage
   */
  async createLeadStage(stageData) {
    const response = await api.post(`${API_BASE}/lead-stages`, stageData);
    return response.data;
  },

  /**
   * Update a lead stage
   * @param {number} stageId - Stage ID
   * @param {Object} stageData - Stage data
   * @returns {Promise<Object>} - Updated stage
   */
  async updateLeadStage(stageId, stageData) {
    const response = await api.put(`${API_BASE}/lead-stages/${stageId}`, stageData);
    return response.data;
  },

  /**
   * Delete a lead stage
   * @param {number} stageId - Stage ID
   * @returns {Promise<Object>} - Deletion result
   */
  async deleteLeadStage(stageId) {
    const response = await api.delete(`${API_BASE}/lead-stages/${stageId}`);
    return response.data;
  },

  /**
   * Reorder lead stages
   * @param {Array} updates - Array of {id, order} objects
   * @returns {Promise<Object>} - Update result
   */
  async reorderLeadStages(updates) {
    const response = await api.post(`${API_BASE}/lead-stages/reorder`, { stages: updates });
    return response.data;
  },

  /**
   * Get stage statistics
   * @returns {Promise<Object>} - Stage statistics
   */
  async getStageStatistics() {
    const response = await api.get(`${API_BASE}/lead-stages/statistics`);
    return response.data;
  },

  // ==================== Lead Scores ====================

  /**
   * Get lead score details
   * @param {number} leadId - Lead ID
   * @returns {Promise<Object>} - Score breakdown
   */
  async getLeadScore(leadId) {
    const response = await api.get(`${API_BASE}/leads/${leadId}/score`);
    return response.data;
  },

  /**
   * Recalculate lead score
   * @param {number} leadId - Lead ID
   * @returns {Promise<Object>} - New score
   */
  async recalculateScore(leadId) {
    const response = await api.post(`${API_BASE}/leads/${leadId}/score/recalculate`);
    return response.data;
  },

  /**
   * Get score history for a lead
   * @param {number} leadId - Lead ID
   * @param {number} days - Number of days to look back
   * @returns {Promise<Array>} - Score history
   */
  async getScoreHistory(leadId, days = 30) {
    const response = await api.get(`${API_BASE}/leads/${leadId}/score-history`, {
      params: { days },
    });
    return response.data;
  },

  // ==================== Lead Activity ====================

  /**
   * Get lead activity timeline
   * @param {number} leadId - Lead ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} - Activity list
   */
  async getLeadActivity(leadId, params = {}) {
    const response = await api.get(`${API_BASE}/leads/${leadId}/activity`, { params });
    return response.data;
  },

  /**
   * Get lead sessions
   * @param {number} leadId - Lead ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} - Session list
   */
  async getLeadSessions(leadId, params = {}) {
    const response = await api.get(`${API_BASE}/leads/${leadId}/sessions`, { params });
    return response.data;
  },

  // ==================== Scoring Rules ====================

  /**
   * Get all scoring rules
   * @returns {Promise<Array>} - List of scoring rules
   */
  async getScoringRules() {
    const response = await api.get(`${API_BASE}/scoring-rules`);
    return response.data;
  },

  /**
   * Create a scoring rule
   * @param {Object} rule - Rule data
   * @returns {Promise<Object>} - Created rule
   */
  async createScoringRule(rule) {
    const response = await api.post(`${API_BASE}/scoring-rules`, rule);
    return response.data;
  },

  /**
   * Update a scoring rule
   * @param {number} ruleId - Rule ID
   * @param {Object} rule - Rule data
   * @returns {Promise<Object>} - Updated rule
   */
  async updateScoringRule(ruleId, rule) {
    const response = await api.put(`${API_BASE}/scoring-rules/${ruleId}`, rule);
    return response.data;
  },

  /**
   * Delete a scoring rule
   * @param {number} ruleId - Rule ID
   * @returns {Promise<void>}
   */
  async deleteScoringRule(ruleId) {
    await api.delete(`${API_BASE}/scoring-rules/${ruleId}`);
  },

  // ==================== Analytics & Dashboard ====================

  /**
   * Get Sales Radar dashboard data
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} - Dashboard data
   */
  async getDashboard(params = {}) {
    const response = await api.get(`${API_BASE}/analytics/dashboard`, { params });
    return response.data;
  },

  /**
   * Get funnel analytics
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} - Funnel data
   */
  async getFunnelAnalytics(params = {}) {
    const response = await api.get(`${API_BASE}/analytics/funnel`, { params });
    return response.data;
  },

  /**
   * Get lead source analytics
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} - Source analytics
   */
  async getSourceAnalytics(params = {}) {
    const response = await api.get(`${API_BASE}/analytics/sources`, { params });
    return response.data;
  },

  /**
   * Get score distribution
   * @returns {Promise<Object>} - Score distribution data
   */
  async getScoreDistribution() {
    const response = await api.get(`${API_BASE}/analytics/score-distribution`);
    return response.data;
  },

  /**
   * Get lead trends
   * @param {string} period - Time period (7d, 30d, 90d)
   * @returns {Promise<Object>} - Trend data
   */
  async getLeadTrends(period = '30d') {
    const response = await api.get(`${API_BASE}/analytics/trends`, {
      params: { period },
    });
    return response.data;
  },

  // ==================== Real-time ====================

  /**
   * Get active visitors
   * @returns {Promise<Array>} - List of active visitors
   */
  async getActiveVisitors() {
    const response = await api.get(`${API_BASE}/active-visitors`);
    return response.data;
  },

  /**
   * Get recent activity feed
   * @param {number} limit - Number of items
   * @returns {Promise<Array>} - Recent activity
   */
  async getRecentActivity(limit = 20) {
    const response = await api.get(`${API_BASE}/activity-feed`, {
      params: { limit },
    });
    return response.data;
  },

  // ==================== Intent Signals ====================

  /**
   * Get intent signals for a lead
   * @param {number} leadId - Lead ID
   * @returns {Promise<Array>} - Intent signals
   */
  async getIntentSignals(leadId) {
    const response = await api.get(`${API_BASE}/leads/${leadId}/intent-signals`);
    return response.data;
  },

  /**
   * Get high-intent leads
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} - High-intent leads
   */
  async getHighIntentLeads(params = {}) {
    const response = await api.get(`${API_BASE}/high-intent-leads`, { params });
    return response.data;
  },

  // ==================== Scoring Rules ====================

  /**
   * Get all scoring rules
   * @returns {Promise<Object>} - Scoring rules
   */
  async getScoringRules() {
    const response = await api.get(`${API_BASE}/scoring-rules`);
    return response.data;
  },

  /**
   * Create a scoring rule
   * @param {Object} ruleData - Rule data
   * @returns {Promise<Object>} - Created rule
   */
  async createScoringRule(ruleData) {
    const response = await api.post(`${API_BASE}/scoring-rules`, ruleData);
    return response.data;
  },

  /**
   * Update a scoring rule
   * @param {number} ruleId - Rule ID
   * @param {Object} ruleData - Rule data
   * @returns {Promise<Object>} - Updated rule
   */
  async updateScoringRule(ruleId, ruleData) {
    const response = await api.put(`${API_BASE}/scoring-rules/${ruleId}`, ruleData);
    return response.data;
  },

  /**
   * Delete a scoring rule
   * @param {number} ruleId - Rule ID
   * @returns {Promise<Object>} - Deletion result
   */
  async deleteScoringRule(ruleId) {
    const response = await api.delete(`${API_BASE}/scoring-rules/${ruleId}`);
    return response.data;
  },

  /**
   * Test a scoring rule
   * @param {number} ruleId - Rule ID
   * @returns {Promise<Object>} - Test results
   */
  async testScoringRule(ruleId) {
    const response = await api.post(`${API_BASE}/scoring-rules/${ruleId}/test`);
    return response.data;
  },

  /**
   * Reorder scoring rules
   * @param {Array} updates - Array of {id, priority} objects
   * @returns {Promise<Object>} - Update result
   */
  async reorderScoringRules(updates) {
    const response = await api.post(`${API_BASE}/scoring-rules/reorder`, { rules: updates });
    return response.data;
  },

  /**
   * Get scoring weights
   * @returns {Promise<Object>} - Scoring weights
   */
  async getScoringWeights() {
    const response = await api.get(`${API_BASE}/scoring-weights`);
    return response.data;
  },

  /**
   * Update scoring weights
   * @param {Object} weights - Weights by category
   * @returns {Promise<Object>} - Updated weights
   */
  async updateScoringWeights(weights) {
    const response = await api.put(`${API_BASE}/scoring-weights`, weights);
    return response.data;
  },

  // ==================== ML Models ====================

  /**
   * Get all ML model versions
   * @param {Object} params - Query params (model_type, status, limit)
   * @returns {Promise<Object>} - List of model versions
   */
  async getMLModels(params = {}) {
    const response = await api.get(`${API_BASE}/ml/models`, { params });
    return response.data;
  },

  /**
   * Get ML model details
   * @param {string} versionId - Model version ID
   * @returns {Promise<Object>} - Model details
   */
  async getMLModelDetails(versionId) {
    const response = await api.get(`${API_BASE}/ml/models/${versionId}`);
    return response.data;
  },

  /**
   * Deploy an ML model
   * @param {string} versionId - Model version ID
   * @returns {Promise<Object>} - Deployment result
   */
  async deployMLModel(versionId) {
    const response = await api.post(`${API_BASE}/ml/models/${versionId}/deploy`);
    return response.data;
  },

  /**
   * Get ML model metrics
   * @param {string} versionId - Model version ID
   * @param {Object} params - Query params (start_date, end_date)
   * @returns {Promise<Object>} - Model metrics
   */
  async getMLModelMetrics(versionId, params = {}) {
    const response = await api.get(`${API_BASE}/ml/models/${versionId}/metrics`, { params });
    return response.data;
  },

  /**
   * Get training jobs
   * @param {Object} params - Query params (status, limit)
   * @returns {Promise<Object>} - List of training jobs
   */
  async getTrainingJobs(params = {}) {
    const response = await api.get(`${API_BASE}/ml/training/jobs`, { params });
    return response.data;
  },

  /**
   * Start a training job
   * @param {Object} data - Training job config (model_type, parameters)
   * @returns {Promise<Object>} - Created training job
   */
  async startTrainingJob(data) {
    const response = await api.post(`${API_BASE}/ml/training/jobs`, data);
    return response.data;
  },

  /**
   * Get training job details
   * @param {string} jobId - Job ID
   * @returns {Promise<Object>} - Job details
   */
  async getTrainingJobDetails(jobId) {
    const response = await api.get(`${API_BASE}/ml/training/jobs/${jobId}`);
    return response.data;
  },

  /**
   * Get ML features
   * @returns {Promise<Object>} - List of ML features
   */
  async getMLFeatures() {
    const response = await api.get(`${API_BASE}/ml/features`);
    return response.data;
  },

  /**
   * Get ML prediction for a lead
   * @param {number} leadId - Lead ID
   * @param {Object} data - Prediction config (model_type)
   * @returns {Promise<Object>} - Prediction result
   */
  async getMLPrediction(leadId, data = {}) {
    const response = await api.post(`${API_BASE}/ml/predict/${leadId}`, data);
    return response.data;
  },

  /**
   * Get batch ML predictions
   * @param {Object} data - Batch prediction config (lead_ids, model_type)
   * @returns {Promise<Object>} - Prediction results
   */
  async getMLPredictionsBatch(data) {
    const response = await api.post(`${API_BASE}/ml/predict/batch`, data);
    return response.data;
  },

  /**
   * Get A/B tests
   * @param {Object} params - Query params (status, limit)
   * @returns {Promise<Object>} - List of A/B tests
   */
  async getABTests(params = {}) {
    const response = await api.get(`${API_BASE}/ml/abtests`, { params });
    return response.data;
  },

  /**
   * Create an A/B test
   * @param {Object} data - A/B test config
   * @returns {Promise<Object>} - Created A/B test
   */
  async createABTest(data) {
    const response = await api.post(`${API_BASE}/ml/abtests`, data);
    return response.data;
  },

  /**
   * Get A/B test details
   * @param {string} testId - Test ID
   * @returns {Promise<Object>} - Test details
   */
  async getABTestDetails(testId) {
    const response = await api.get(`${API_BASE}/ml/abtests/${testId}`);
    return response.data;
  },

  /**
   * Start an A/B test
   * @param {string} testId - Test ID
   * @returns {Promise<Object>} - Start result
   */
  async startABTest(testId) {
    const response = await api.post(`${API_BASE}/ml/abtests/${testId}/start`);
    return response.data;
  },

  /**
   * End an A/B test
   * @param {string} testId - Test ID
   * @returns {Promise<Object>} - End result with winner
   */
  async endABTest(testId) {
    const response = await api.post(`${API_BASE}/ml/abtests/${testId}/end`);
    return response.data;
  },

  /**
   * Get ML dashboard data
   * @returns {Promise<Object>} - Dashboard data
   */
  async getMLDashboard() {
    const response = await api.get(`${API_BASE}/ml/dashboard`);
    return response.data;
  },

  /**
   * Schedule automatic retraining
   * @param {Object} data - Schedule config (model_type, schedule, parameters)
   * @returns {Promise<Object>} - Schedule result
   */
  async scheduleRetraining(data) {
    const response = await api.post(`${API_BASE}/ml/retraining/schedule`, data);
    return response.data;
  },

  // ==================== Conversion Prediction ====================

  /**
   * Get conversion prediction for a lead
   * @param {number} leadId - Lead ID
   * @returns {Promise<Object>} - Conversion prediction with all details
   */
  async getConversionPrediction(leadId) {
    const response = await api.get(`${API_BASE}/conversion/predict/${leadId}`);
    return response.data;
  },

  /**
   * Get batch conversion predictions
   * @param {Object} data - Batch config (lead_ids)
   * @returns {Promise<Object>} - Batch predictions
   */
  async getConversionPredictionsBatch(data) {
    const response = await api.post(`${API_BASE}/conversion/predict/batch`, data);
    return response.data;
  },

  /**
   * Get comprehensive conversion insights for a lead
   * @param {number} leadId - Lead ID
   * @returns {Promise<Object>} - Insights with recommendations
   */
  async getConversionInsights(leadId) {
    const response = await api.get(`${API_BASE}/conversion/insights/${leadId}`);
    return response.data;
  },

  /**
   * Get detailed conversion factors for a lead
   * @param {number} leadId - Lead ID
   * @returns {Promise<Object>} - Factor breakdown
   */
  async getConversionFactors(leadId) {
    const response = await api.get(`${API_BASE}/conversion/factors/${leadId}`);
    return response.data;
  },

  /**
   * Get deal size prediction for a lead
   * @param {number} leadId - Lead ID
   * @returns {Promise<Object>} - Deal size prediction
   */
  async getDealSizePrediction(leadId) {
    const response = await api.get(`${API_BASE}/conversion/deal-size/${leadId}`);
    return response.data;
  },

  /**
   * Get close timeline prediction for a lead
   * @param {number} leadId - Lead ID
   * @returns {Promise<Object>} - Timeline prediction
   */
  async getCloseTimelinePrediction(leadId) {
    const response = await api.get(`${API_BASE}/conversion/timeline/${leadId}`);
    return response.data;
  },

  // ==================== Next Best Actions ====================

  /**
   * Get AI-powered action recommendations for a lead
   * @param {number} leadId - Lead ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} - Action recommendations
   */
  async getActionRecommendations(leadId, params = {}) {
    const response = await api.get(`${API_BASE}/actions/recommendations/${leadId}`, { params });
    return response.data;
  },

  /**
   * Execute a recommended action
   * @param {Object} actionData - Action execution data
   * @returns {Promise<Object>} - Execution result
   */
  async executeAction(actionData) {
    const response = await api.post(`${API_BASE}/actions/execute`, actionData);
    return response.data;
  },

  /**
   * Submit feedback for an action recommendation
   * @param {Object} feedbackData - Feedback data
   * @returns {Promise<Object>} - Confirmation
   */
  async submitActionFeedback(feedbackData) {
    const response = await api.post(`${API_BASE}/actions/feedback`, feedbackData);
    return response.data;
  },

  /**
   * Get action templates
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} - List of templates
   */
  async getActionTemplates(params = {}) {
    const response = await api.get(`${API_BASE}/actions/templates`, { params });
    return response.data;
  },

  /**
   * Create a new action template
   * @param {Object} template - Template data
   * @returns {Promise<Object>} - Created template
   */
  async createActionTemplate(template) {
    const response = await api.post(`${API_BASE}/actions/templates`, template);
    return response.data;
  },

  /**
   * Update an action template
   * @param {number} templateId - Template ID
   * @param {Object} template - Template data
   * @returns {Promise<Object>} - Updated template
   */
  async updateActionTemplate(templateId, template) {
    const response = await api.put(`${API_BASE}/actions/templates/${templateId}`, template);
    return response.data;
  },

  /**
   * Delete an action template
   * @param {number} templateId - Template ID
   * @returns {Promise<void>}
   */
  async deleteActionTemplate(templateId) {
    await api.delete(`${API_BASE}/actions/templates/${templateId}`);
  },

  /**
   * Render an action template with variables
   * @param {number} templateId - Template ID
   * @param {Object} data - Variables to render
   * @returns {Promise<Object>} - Rendered template
   */
  async renderActionTemplate(templateId, data) {
    const response = await api.post(`${API_BASE}/actions/templates/${templateId}/render`, data);
    return response.data;
  },

  /**
   * Get action analytics
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} - Analytics data
   */
  async getActionAnalytics(params = {}) {
    const response = await api.get(`${API_BASE}/actions/analytics`, { params });
    return response.data;
  },

  // ==================== Churn Prediction (SR-24) ====================

  /**
   * Get churn prediction for a customer
   * @param {number} customerId - Customer/Lead ID
   * @returns {Promise<Object>} - Churn prediction with risk factors
   */
  async getChurnPrediction(customerId) {
    const response = await api.get(`${API_BASE}/churn/predict/${customerId}`);
    return response.data;
  },

  /**
   * Get batch churn predictions for multiple customers
   * @param {Array<number>} customerIds - List of customer IDs
   * @returns {Promise<Object>} - Batch predictions
   */
  async getBatchChurnPredictions(customerIds) {
    const response = await api.post(`${API_BASE}/churn/batch`, { customer_ids: customerIds });
    return response.data;
  },

  /**
   * Get churn dashboard data
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} - Dashboard data
   */
  async getChurnDashboard(params = {}) {
    const response = await api.get(`${API_BASE}/churn/dashboard`, { params });
    return response.data;
  },

  /**
   * Get churn alerts for high-risk customers
   * @param {Object} params - Query parameters (min_risk_level, limit)
   * @returns {Promise<Object>} - List of alerts
   */
  async getChurnAlerts(params = {}) {
    const response = await api.get(`${API_BASE}/churn/alerts`, { params });
    return response.data;
  },

  /**
   * Get top churn risk factors across all customers
   * @returns {Promise<Object>} - Risk factors summary
   */
  async getTopChurnRiskFactors() {
    const response = await api.get(`${API_BASE}/churn/risk-factors`);
    return response.data;
  },

  /**
   * Record an intervention for a customer
   * @param {number} customerId - Customer ID
   * @param {Object} interventionData - Intervention details
   * @returns {Promise<Object>} - Confirmation
   */
  async recordChurnIntervention(customerId, interventionData) {
    const response = await api.post(`${API_BASE}/churn/intervention/${customerId}`, interventionData);
    return response.data;
  },

  // ==================== Lookalike Modeling (SR-25) ====================

  /**
   * Get all lookalike profiles
   * @returns {Promise<Object>} - List of profiles
   */
  async getLookalikeProfiles() {
    const response = await api.get(`${API_BASE}/lookalike/profiles`);
    return response.data;
  },

  /**
   * Create a lookalike profile from best customers
   * @param {Object} profileData - Profile data with customer_ids, name, etc.
   * @returns {Promise<Object>} - Created profile
   */
  async createLookalikeProfile(profileData) {
    const response = await api.post(`${API_BASE}/lookalike/profiles`, profileData);
    return response.data;
  },

  /**
   * Get a specific lookalike profile
   * @param {string} profileId - Profile ID
   * @returns {Promise<Object>} - Profile details
   */
  async getLookalikeProfile(profileId) {
    const response = await api.get(`${API_BASE}/lookalike/profiles/${profileId}`);
    return response.data;
  },

  /**
   * Score a lead's similarity to a profile
   * @param {number} leadId - Lead ID
   * @param {Object} data - Profile ID and optional weights
   * @returns {Promise<Object>} - Similarity score
   */
  async scoreLeadSimilarity(leadId, data) {
    const response = await api.post(`${API_BASE}/lookalike/score/${leadId}`, data);
    return response.data;
  },

  /**
   * Score multiple leads against a profile
   * @param {Object} data - Profile ID, lead IDs, min_score, limit
   * @returns {Promise<Object>} - Scored leads
   */
  async batchScoreSimilarity(data) {
    const response = await api.post(`${API_BASE}/lookalike/batch-score`, data);
    return response.data;
  },

  /**
   * Create a lookalike segment
   * @param {Object} segmentData - Segment configuration
   * @returns {Promise<Object>} - Created segment
   */
  async createLookalikeSegment(segmentData) {
    const response = await api.post(`${API_BASE}/lookalike/segments`, segmentData);
    return response.data;
  },

  /**
   * Export a lookalike segment for ad targeting
   * @param {string} segmentId - Segment ID
   * @param {string} format - Export format (csv_fields, facebook, linkedin, google)
   * @returns {Promise<Object>} - Export data
   */
  async exportLookalikeSegment(segmentId, format = 'csv_fields') {
    const response = await api.get(`${API_BASE}/lookalike/segments/${segmentId}/export`, {
      params: { format }
    });
    return response.data;
  },

  /**
   * Find leads similar to a reference lead
   * @param {number} leadId - Reference lead ID
   * @param {Object} params - limit, min_score
   * @returns {Promise<Object>} - Similar leads
   */
  async findSimilarLeads(leadId, params = {}) {
    const response = await api.get(`${API_BASE}/lookalike/similar/${leadId}`, { params });
    return response.data;
  },

  // ==================== Settings ====================

  /**
   * Get Sales Radar settings
   * @returns {Promise<Object>} - Settings
   */
  async getSettings() {
    const response = await api.get(`${API_BASE}/settings`);
    return response.data;
  },

  /**
   * Update Sales Radar settings
   * @param {Object} settings - Settings to update
   * @returns {Promise<Object>} - Updated settings
   */
  async updateSettings(settings) {
    const response = await api.put(`${API_BASE}/settings`, settings);
    return response.data;
  },

  /**
   * Get tracking script snippet
   * @returns {Promise<Object>} - Script snippet and installation instructions
   */
  async getTrackingScript() {
    const response = await api.get(`${API_BASE}/tracking-script`);
    return response.data;
  },

  // ==================== Outreach Timing ====================

  /**
   * Get timing analytics for a project
   * @param {number} projectId - Project ID
   * @param {number} dateRange - Date range in days
   * @returns {Promise<Object>} - Timing analytics
   */
  async getTimingAnalytics(projectId, dateRange = 90) {
    const response = await api.get(`${API_BASE}/timing/analytics`, {
      params: { project_id: projectId, date_range: dateRange }
    });
    return response.data;
  },

  /**
   * Get timing profile for a specific lead
   * @param {number} leadId - Lead ID
   * @returns {Promise<Object>} - Lead timing profile
   */
  async getLeadTimingProfile(leadId) {
    const response = await api.get(`${API_BASE}/timing/lead/${leadId}/profile`);
    return response.data;
  },

  /**
   * Get optimal outreach time for a lead
   * @param {number} leadId - Lead ID
   * @param {string} channel - Contact channel (optional)
   * @param {string} urgency - Urgency level (high, medium, low)
   * @returns {Promise<Object>} - Optimal time recommendation
   */
  async getOptimalOutreachTime(leadId, channel = null, urgency = 'medium') {
    const params = { urgency };
    if (channel) params.channel = channel;
    const response = await api.get(`${API_BASE}/timing/lead/${leadId}/optimal`, { params });
    return response.data;
  },

  /**
   * Get channel-specific timing recommendations
   * @param {string} channel - Contact channel
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} - Channel timing data
   */
  async getChannelTiming(channel, projectId) {
    const response = await api.get(`${API_BASE}/timing/channel/${channel}`, {
      params: { project_id: projectId }
    });
    return response.data;
  },

  /**
   * Get timing heatmap data
   * @param {number} projectId - Project ID
   * @param {string} channel - Optional channel filter
   * @returns {Promise<Object>} - Heatmap data
   */
  async getTimingHeatmap(projectId, channel = null) {
    const params = { project_id: projectId };
    if (channel) params.channel = channel;
    const response = await api.get(`${API_BASE}/timing/heatmap`, { params });
    return response.data;
  },

  /**
   * Get calendar suggestions for multiple leads
   * @param {Array} leadIds - Array of lead IDs
   * @param {number} dateRangeDays - Planning horizon in days
   * @returns {Promise<Object>} - Calendar suggestions
   */
  async getCalendarSuggestions(leadIds, dateRangeDays = 7) {
    const response = await api.post(`${API_BASE}/timing/calendar-suggestions`, {
      lead_ids: leadIds,
      date_range_days: dateRangeDays
    });
    return response.data;
  },

  // ==================== Account Radar Chart ====================

  /**
   * Get radar chart data for an account/lead
   * @param {number} leadId - Lead ID
   * @returns {Promise<Object>} - Radar chart data with dimensions
   */
  async getAccountRadarData(leadId) {
    const response = await api.get(`${API_BASE}/account/${leadId}/radar`);
    return response.data;
  },

  /**
   * Get radar chart data with average comparison
   * @param {number} leadId - Lead ID
   * @param {number} projectId - Project ID for comparison
   * @returns {Promise<Object>} - Radar comparison data
   */
  async getAccountRadarComparison(leadId, projectId) {
    const response = await api.get(`${API_BASE}/account/${leadId}/radar/comparison`, {
      params: { project_id: projectId }
    });
    return response.data;
  },

  /**
   * Get available radar chart dimensions
   * @returns {Promise<Object>} - Dimension configurations
   */
  async getRadarDimensions() {
    const response = await api.get(`${API_BASE}/radar/dimensions`);
    return response.data;
  },

  // ==================== Activity Heatmap ====================

  /**
   * Get activity heatmap data
   * @param {number} projectId - Project ID
   * @param {Object} options - Filter options
   * @returns {Promise<Object>} - Heatmap data (7x24 matrix)
   */
  async getActivityHeatmap(projectId, options = {}) {
    const params = {
      project_id: projectId,
      date_range: options.date_range || 30,
      ...options
    };
    const response = await api.get(`${API_BASE}/activity/heatmap`, { params });
    return response.data;
  },

  /**
   * Get detailed activities for a specific day/hour
   * @param {number} projectId - Project ID
   * @param {Object} options - Filter options including day_of_week and hour
   * @returns {Promise<Object>} - Drilldown activity data
   */
  async getActivityDrilldown(projectId, options) {
    const params = {
      project_id: projectId,
      ...options
    };
    const response = await api.get(`${API_BASE}/activity/drilldown`, { params });
    return response.data;
  },

  // ==================== AI Insights ====================

  /**
   * Get comprehensive AI insights for a lead
   * @param {number} leadId - Lead ID
   * @returns {Promise<Object>} - AI insights data
   */
  async getAIInsights(leadId) {
    const response = await api.get(`${API_BASE}/insights/${leadId}`);
    return response.data;
  },
};

export default salesRadarService;
